import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { ShieldCheck } from 'lucide-react';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState, ErrorState, Skeleton } from '../../components/ui/states';
import { myTasksApi, myTasksKeys } from '../../features/my-tasks/api';
import { errorMessage } from '../../lib/error-message';

/**
 * "Ai đã xem hồ sơ hiệu suất của tôi" — spec mục 14.
 *
 * API `GET /me/access-log` có từ `APM-403` và đã có test, nhưng cho tới đây thì
 * không có lối vào nào trong giao diện người dùng thường. Spec xếp minh bạch
 * với người bị đánh giá **ngang hàng** với phân quyền và audit; một endpoint mà
 * chỉ người biết đường gõ URL mới mở được thì không thoả yêu cầu đó.
 *
 * Không đòi quyền gì: đây là dữ liệu về chính người đang đăng nhập.
 */
export const Route = createFileRoute('/_app/privacy')({ component: PrivacyPage });

function PrivacyPage(): ReactNode {
  const { t } = useTranslation();

  const log = useQuery({
    queryKey: myTasksKeys.accessLog(),
    queryFn: myTasksApi.accessLog,
  });

  return (
    <div className="space-y-4">
      <header>
        <h1 className="flex items-center gap-2 text-xl font-semibold">
          <ShieldCheck className="h-5 w-5" aria-hidden />
          {t('privacy.title')}
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{t('privacy.subtitle')}</p>
      </header>

      {log.isPending && <Skeleton className="h-48 w-full" />}
      {log.isError && (
        <ErrorState message={errorMessage(log.error, t)} onRetry={() => void log.refetch()} />
      )}

      {log.isSuccess &&
        (log.data.length === 0 ? (
          // Trạng thái rỗng ở đây là tin tốt, không phải thiếu dữ liệu — nói rõ
          // điều đó thay vì để người dùng đoán.
          <EmptyState title={t('privacy.emptyTitle')} description={t('privacy.emptyHelp')} />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs text-muted-foreground">
                <th className="py-2 font-medium">{t('privacy.column.viewer')}</th>
                <th className="py-2 font-medium">{t('privacy.column.scope')}</th>
                <th className="py-2 font-medium">{t('privacy.column.reason')}</th>
                <th className="py-2 text-right font-medium">{t('privacy.column.when')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {log.data.map((entry) => (
                <tr key={entry.id}>
                  <td className="py-2">{entry.viewerName ?? t('privacy.unknownViewer')}</td>
                  <td className="py-2 font-mono text-xs text-muted-foreground">{entry.scope}</td>
                  <td className="py-2 text-muted-foreground">{entry.reason ?? '—'}</td>
                  <td className="py-2 text-right tabular-nums text-muted-foreground">
                    {new Date(entry.accessedAt).toLocaleString('vi-VN')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ))}
    </div>
  );
}
